import { useEffect, useMemo, useState } from "react";
import { bool, func, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useEstimateStructure, PercentSettingContents } from "@tools";
import { Dialog, Button, Tag, Accordion, Checkbox } from "@components";
import { Grid, Switch } from "@material-ui/core";
import { Percent } from "@assets";

export const BreakdownAutomation = observer(({
  open,
  workType,
  section,
  onClose,
}) => {
  
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const [ localPercent, setPercent ] = useState(10);
  const [ automationUsed, setAutomationUsed ] = useState(false);
  const [ usedWorkTypes, setUsedWorkTypes ] = useState([]);
  const [ toAllSections, setToAllSections ] = useState(false);
  
  const { id, percent, of, name, color, hasPercent } = workType;
  
  const disabled = useMemo(() => (
    automationUsed
      ? (!usedWorkTypes.length || typeof localPercent !== "number")
      : !workType.hasPercent
  ), [ automationUsed, usedWorkTypes, localPercent, hasPercent ]);
  
  useEffect(() => {
    setPercent(percent || 10);
    setAutomationUsed(true);
  }, [ percent ]);
  
  useEffect(() => {
    setUsedWorkTypes(of || []);
  }, [ of ]);
  
  useEffect(() => {
    if(open)
      setToAllSections(
        section.parentOrder === 0 && !structure.sections.find((section, i) => (
          i > 0 && (
            section.hasValues
            || section.workTypes.find(wT => wT.id === id && wT.hasPercent)
          )
        ))
      )
  }, [ open ]);
  
  const availableWorkTypes = useMemo(() => (
    section.taskWorkTypes?.filter(wT => wT.id !== workType.id)
  ), [section?.taskWorkTypes]);
  
  const blacklistedWorkTypes = useMemo(() => {
    return structure.blacklistedAutomationWorkTypesPerSection(id);
  }, [section?.usedWorkTypes, structure.sections]);
  
  const handleExpand = () => {
    setAutomationUsed((state) => !state);
  };
  
  const saveEstimationSettings = () => {
    onClose();
    structure.historyManager.startGroup();
    if(toAllSections) {
      if(automationUsed)
        structure.setWorkTypeAutomation(workType.id, localPercent, usedWorkTypes ? [ ...usedWorkTypes ] : []);
      else
        structure.setWorkTypeAutomation(workType.id, 0);
    } else {
      if (automationUsed) {
        workType.setAutomation(localPercent, usedWorkTypes ? [ ...usedWorkTypes ] : []);
        section.resetPercentValues(workType.id, true, false, true);
      } else {
        workType.setAutomation(0, undefined);
        section.removeAllWorkTypeOverrides(workType.id);
      }
    }
    
    structure.historyManager.stopGroup();
  };

  const actions = (
    <>
      <Button
        variant="outlined"
        className="w-max"
        onClick={onClose}
      >
        {t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        className="w-max"
        onClick={saveEstimationSettings}
        disabled={disabled}
      >
        {t("common.apply")}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      title={
        <Grid container alignItems="center">
          <h2>{t("views.editor.dialogs.automation.title")}</h2>
          <Tag color={color} text={name} className="ml-2 mt-1-5" />
        </Grid>
      }
      onClose={onClose}
      actions={actions}
      width={600}
    >
      <Grid item container alignItems="flex-start">
        <Accordion
          hideExpandButton
          expanded={automationUsed}
          title={
            <Grid item container justifyContent="space-between" alignItems="center">
              <Grid item container wrap="nowrap" alignItems="center" className="w-max">
                <Percent className="color-primary text-2xl mr-2" />
                {t("views.editor.dialogs.automation.enable")}
                {": "}
              </Grid>
              <Switch
                color="primary"
                checked={automationUsed}
                onChange={handleExpand}
                name={
                  automationUsed
                    ? t("views.editor.dialogs.automation.disable")
                    : t("views.editor.dialogs.automation.enable")
                }
              />
            </Grid>
          }
          variant="info"
          bordered={false}
          titleColored
        >
          <PercentSettingContents
            percent={localPercent}
            usedWorkTypes={usedWorkTypes}
            availableWorkTypes={availableWorkTypes}
            blacklistedWorkTypes={blacklistedWorkTypes}
            onPercentChange={setPercent}
            onSelectWorkType={setUsedWorkTypes}
            editedWorkTypeName={name}
          />
        </Accordion>
        <Grid item container className="mt-3 px-2 gap-2">
          <Checkbox
            name="applyToAllSections"
            checked={toAllSections}
            label={t("common.all_sections_apply")}
            onChange={() => setToAllSections(!toAllSections)}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
});

BreakdownAutomation.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  workType: object.isRequired,
  section: object.isRequired,
};
